import React, { useEffect, useRef } from 'react';

import { IFormFieldInputBaseProps } from '@components/form-fields';
import { MAIN_THEME } from '@styles';
import { Editor } from '@tinymce/tinymce-react';
import { TinyMCEApiKey } from '@utils/env-utils';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface IEnhancedEditor extends IFormFieldInputBaseProps {
  name: string;
  title: string;
  placeholder: string;
  error?: string;
  value: string;
  onChange: (name: string, value: string) => void;
  onBlur?: (name: string) => void;
  disabled?: boolean;
  htmlToAdd?: string;
  addButton?: boolean;
  selectOptions?: Record<string, string>;
  onSelect?: (value: string) => void;
  selectValue?: string;
  customStyles?: {
    containerStyles?: string;
    labelStyles?: string;
    inputStyles?: string;
    errorStyles?: string;
  };
  width?: string;
}

const useStyles = makeStyles()(() => ({
  label: {
    display: 'block',
    color: MAIN_THEME.color.black,
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    marginBottom: '13px'
  },
  error: {
    color: 'red',
    margin: '5px 0 0',
    display: 'block',
    fontSize: MAIN_THEME.fontSize.xs,
    minHeight: '15px',
    textAlign: 'right'
  },
  editorContainer: {
    height: '280px',
    minHeight: '280px',
    maxHeight: '280px',
    overflowY: 'auto',
    '.tox-tinymce': {
      fontFamily: MAIN_THEME.font.regular,
      border: `1px solid ${MAIN_THEME.color.white86}`,
      borderRadius: '5px'
    },
    '.tox': {
      '.tox-edit-area': { '::before': { border: 'none' } }
    }
  }
}));

const TextEditorFormField: React.FC<IEnhancedEditor> = ({
  name,
  title,
  placeholder,
  error,
  value,
  onChange,
  onBlur = () => undefined,
  disabled = false,
  htmlToAdd = '',
  addButton = false,
  selectOptions = {},
  onSelect,
  customStyles = {},
  width = '100%',
  selectValue = ''
}) => {
  const { classes } = useStyles();
  const editorRef = useRef<any>(null);

  const htmlRef = useRef<string>(htmlToAdd ?? '');
  useEffect(() => {
    htmlRef.current = htmlToAdd ?? '';
  }, [htmlToAdd]);

  return (
    <div style={{ position: 'relative', width }}>
      <span className={clsx(classes.label, customStyles.labelStyles)}>
        {title}
      </span>
      <div
        className={clsx(classes.editorContainer, customStyles.containerStyles)}
      >
        <Editor
          key={selectValue || 'default'}
          apiKey={TinyMCEApiKey}
          onInit={(_, editor) => {
            editorRef.current = editor;
          }}
          init={{
            directionality: 'ltr',
            plugins: 'lists advlist',
            menubar: false,
            statusbar: false,
            placeholder,
            branding: false,
            // NOTE: let TinyMCE handle Enter for lists & paragraphs
            toolbar:
              'undo redo | bold italic underline strikethrough | forecolor backcolor | numlist bullist outdent indent | customSelectClause customInsertButton',
            setup: (editor) => {
              // ❌ Removed the Enter override that forced <br />
              /*
              editor.on('keydown', (e) => {
                if (e.key === 'Enter' && e.shiftKey) {
                  e.preventDefault();
                  editor.insertContent('<br />');
                }
              });
              */

              if (addButton) {
                editor.ui.registry.addButton('customInsertButton', {
                  text: 'Add Clause',
                  enabled: !disabled,
                  onAction: () => {
                    editor.execCommand(
                      'mceInsertContent',
                      false,
                      htmlRef.current ?? ''
                    );
                  }
                });
              }

              if (Object.keys(selectOptions ?? {}).length > 0) {
                editor.ui.registry.addMenuButton('customSelectClause', {
                  text:
                    (selectOptions && selectOptions[selectValue]) ||
                    'Select Clause',
                  fetch: (callback: any) => {
                    const items = Object.entries(selectOptions ?? {}).map(
                      ([id, label]) => ({
                        type: 'menuitem',
                        text: label,
                        onAction: () => onSelect?.(id)
                      })
                    );
                    callback(items);
                  }
                });
              }
            },
            height: 280,
            resize: false,
            content_style: `
              @font-face {
                font-family: 'SourceSansPro-Regular';
                src: url(https://ufa-cdn.baibars.club/ufa-app-portal/fonts/SourceSansPro-Regular/SourceSansPro-Regular.ttf);
              }
              .tox-tinymce {
                font-family: 'SourceSansPro-Regular';
                border: 1px solid ${MAIN_THEME.color.white86};
                border-radius: 5px;
              }
              .tox .tox-edit-area::before { border: none !important; }
              body {
                font-family: 'SourceSansPro-Regular';
                font-size: 14px;
                background-color: ${MAIN_THEME.color.white98};
                ${disabled ? `color: rgba(0, 0, 0, 0.38); cursor: not-allowed;` : ''}
              }
              .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
                color: ${MAIN_THEME.color.black25};
                position: absolute;
                font-style: italic;
                font-family: 'SourceSansPro-Regular';
                font-size: 14px;
              }
            `
          }}
          value={value ?? ''}
          onEditorChange={(html) => {
            onChange(name, html ?? '');
          }}
          onBlur={() => onBlur(name)}
          disabled={disabled}
        />
      </div>
      <span className={clsx(classes.error, customStyles.errorStyles)}>
        {error}
      </span>
    </div>
  );
};

export default TextEditorFormField;
